import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, NavLink, useLocation, Navigate } from "react-router-dom";
import { LayoutDashboard, MessageSquare, ClipboardList, Users, UserPlus, Settings2, Bot, Sparkles, Bell, ShieldCheck, LogOut, Tags, PanelLeftClose, PanelLeftOpen, Wand2, Activity, Receipt, Wrench, MapPin, Wallet, Zap, QrCode } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { api } from "@/lib/api";
import { AuthProvider, useAuth } from "@/lib/auth";
import Dashboard from "@/pages/Dashboard";
import Simulator from "@/pages/Simulator";
import WorkOrders from "@/pages/WorkOrders";
import Technicians from "@/pages/Technicians";
import LiveMap from "@/pages/LiveMap";
import { WODoneNotifier } from "@/components/WODoneNotifier";
import SettingsPage from "@/pages/Settings";
import Estimasi from "@/pages/Estimasi";
import Login from "@/pages/Login";
import Admins from "@/pages/Admins";
import Tarif from "@/pages/Tarif";
import AIRules from "@/pages/AIRules";
import LLMSettings from "@/pages/LLMSettings";
import GoogleSync from "@/pages/GoogleSync";
import RekapPenagihan from "@/pages/RekapPenagihan";
import BillingDetail from "@/pages/BillingDetail";
import PublicBilling from "@/pages/PublicBilling";
import QuickOrder from "@/pages/QuickOrder";
import QuickOrderPublic from "@/pages/QuickOrderPublic";
import QRQuickOrderPage from "@/pages/QRQuickOrderPage";
import ErrorBoundary from "@/components/ErrorBoundary";
import Tracking from "@/pages/Tracking";
import TechGps from "@/pages/TechGps";
import TechLogin from "@/pages/TechLogin";
import TechRekap from "@/pages/TechRekap";
import ViewInvoice from "@/pages/ViewInvoice";
import Commissions from "@/pages/Commissions";
import Personel from "@/pages/Personel";
import KomisiTeknisi from "@/pages/KomisiTeknisi";
import Payroll from "@/pages/Payroll";
import WaMessages from "@/pages/WaMessages";
import Customer from "@/pages/Customer";
import { AdminAiChat } from "@/components/AdminAiChat";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/simulator", label: "AI + WhatsApp", icon: MessageSquare, testid: "nav-simulator" },
  { to: "/pesan-otomatis", label: "Kelola Pesan Otomatis", icon: MessageSquare, testid: "nav-wa-messages" },
  { to: "/work-orders", label: "Work Order", icon: ClipboardList, testid: "nav-workorders" },
  { to: "/technicians", label: "Tim Armada", icon: Users, testid: "nav-technicians" },
  { to: "/live-map", label: "Live Map Armada", icon: MapPin, testid: "nav-live-map" },
  { to: "/personel", label: "Data Karyawan", icon: Users, testid: "nav-personel" },
  { to: "/customer", label: "Customer", icon: UserPlus, testid: "nav-customer" },
  { to: "/tarif", label: "Tarif & Harga", icon: Tags, testid: "nav-tarif" },
  { to: "/rekap-penagihan", label: "Rekap Penagihan", icon: Receipt, testid: "nav-rekap" },
  { to: "/commissions", label: "Jasa Teknisi", icon: Wrench, testid: "nav-commissions" },
  { to: "/komisi-teknisi", label: "Komisi Teknisi", icon: Wrench, testid: "nav-komisi-teknisi" },
  { to: "/payroll", label: "Payroll", icon: Wallet, testid: "nav-payroll" },
  { to: "/estimasi", label: "Estimasi Durasi", icon: Sparkles, testid: "nav-estimasi" },
  { to: "/ai-rules", label: "AI Rules", icon: Wand2, testid: "nav-ai-rules", superOnly: true },
  { to: "/llm-settings", label: "LLM Provider", icon: Zap, testid: "nav-llm-settings", superOnly: true },
  { to: "/google-sync", label: "Google Sync", icon: Activity, testid: "nav-google-sync" },
  { to: "/qr-quick-order", label: "QR Quick Order", icon: QrCode, testid: "nav-qr-quick-order" },
  { to: "/admins", label: "Admin", icon: ShieldCheck, testid: "nav-admins", superOnly: true },
  { to: "/settings", label: "Pengaturan", icon: Settings2, testid: "nav-settings", superOnly: true },
];

function Sidebar({ collapsed, onToggle }) {
  const { user } = useAuth();
  const isSuper = (user?.role || "super") === "super";
  const visibleNav = NAV.filter(n => !n.superOnly || isSuper);
  const [notif, setNotif] = useState({count: 0, items: [], latest_ts: null});
  const lastSeenTsRef = useRef(null);
  const notifPermRef = useRef(typeof window !== "undefined" && "Notification" in window ? Notification.permission : "unsupported");

  // Minta izin Browser Notification API sekali saat load pertama
  useEffect(() => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission === "default") {
      // Delay sedikit supaya user sudah settle di dashboard
      const t = setTimeout(() => {
        Notification.requestPermission().then(p => { notifPermRef.current = p; });
      }, 2000);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    let stop = false;
    async function poll() {
      try {
        const n = await api.notifications();
        if (stop) return;
        setNotif(n);
        // Kalau ada pesan customer BARU (latest_ts berubah), trigger pop-up
        const currLatest = n.latest_ts;
        const prevSeen = lastSeenTsRef.current;
        if (currLatest && currLatest !== prevSeen && n.count > 0) {
          const isFirstLoad = prevSeen === null;
          lastSeenTsRef.current = currLatest;
          if (!isFirstLoad && typeof window !== "undefined"
              && "Notification" in window
              && Notification.permission === "granted") {
            const topItem = n.items?.[0];
            const title = "🔔 Butuh Admin — GLOBAL AC";
            const body = topItem
              ? `${topItem.customer_name}: ${topItem.text}`
              : `${n.count} pesan customer menunggu`;
            try {
              const notification = new Notification(title, {
                body, tag: "handover-notif",
                icon: "/logo.png",
                requireInteraction: true,
              });
              notification.onclick = () => {
                window.focus();
                window.location.href = "/simulator";
                notification.close();
              };
            } catch {}
          }
        }
      } catch {}
    }
    poll();
    // Poll setiap 20 detik (badge notifikasi)
    const t = setInterval(poll, 20000);
    return () => { stop = true; clearInterval(t); };
  }, []);

  return (
    <aside
      className={`${collapsed ? "w-16" : "w-60"} shrink-0 border-r border-border bg-white flex flex-col h-screen sticky top-0 transition-[width] duration-200 ease-out`}
      data-testid="sidebar"
      data-collapsed={collapsed ? "true" : "false"}
    >
      <div className={`${collapsed ? "px-2" : "px-5"} pt-5 pb-4 border-b border-border`}>
        <div className={`flex items-center ${collapsed ? "justify-center" : "gap-3"}`}>
          <img src="/logo.png" alt="Global AC" className="w-10 h-10 rounded-full object-contain bg-white ring-1 ring-zinc-200 shrink-0" />
          {!collapsed && (
            <div className="min-w-0">
              <div className="font-['Manrope'] font-bold leading-none text-[15px] tracking-tight">GLOBAL AC</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1 truncate">BANJARBARU</div>
            </div>
          )}
        </div>
        <button
          onClick={onToggle}
          data-testid="sidebar-toggle-btn"
          title={collapsed ? "Buka sidebar" : "Lipat sidebar"}
          className="mt-3 w-full inline-flex items-center justify-center gap-1.5 h-7 rounded-sm border border-border bg-zinc-50 hover:bg-zinc-100 text-zinc-600 text-[11px] font-semibold transition-colors"
        >
          {collapsed
            ? <PanelLeftOpen className="w-3.5 h-3.5" strokeWidth={1.8} />
            : <><PanelLeftClose className="w-3.5 h-3.5" strokeWidth={1.8} /> Lipat</>}
        </button>
      </div>
      <nav className={`${collapsed ? "p-2" : "p-3"} flex flex-col gap-1 flex-1 overflow-y-auto`}>
        {visibleNav.map(n => {
          const showBadge = n.to === "/simulator" && notif.count > 0;
          return (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={n.testid}
              title={collapsed ? n.label : undefined}
              className={({isActive}) =>
                `flex items-center ${collapsed ? "justify-center" : "gap-2.5"} ${collapsed ? "px-2" : "px-3"} py-2 rounded-sm text-[13px] transition-colors duration-200 relative ${
                  isActive
                    ? "bg-[#E65100]/10 text-[#E65100] font-semibold"
                    : "text-zinc-700 hover:bg-zinc-100"
                }`
              }
            >
              <n.icon className="w-4 h-4 shrink-0" strokeWidth={1.7} />
              {!collapsed && n.label}
              {showBadge && (
                collapsed ? (
                  <span className="absolute -top-1 -right-1 inline-flex items-center justify-center text-white bg-red-600 text-[9px] font-bold rounded-full min-w-[18px] h-[18px] px-1 animate-pulse ring-2 ring-white shadow" data-testid="notif-badge">
                    {notif.count > 9 ? "9+" : notif.count}
                  </span>
                ) : (
                  <span className="ml-auto inline-flex items-center gap-1 text-white bg-red-600 text-[10px] font-bold rounded-full px-2 py-0.5 animate-pulse shadow-md" data-testid="notif-badge">
                    <Bell className="w-3 h-3" />
                    {notif.needs_admin_count > 0 && `${notif.needs_admin_count} Butuh`}
                    {notif.needs_admin_count > 0 && notif.unread_count > 0 && " · "}
                    {notif.unread_count > 0 && `${notif.unread_count} Unread`}
                  </span>
                )
              )}
            </NavLink>
          );
        })}
      </nav>
      <div className={`${collapsed ? "p-2" : "p-3"} border-t border-border space-y-2`}>
        <UserFooter collapsed={collapsed} />
        {!collapsed && (
          <div className="flex items-center gap-2 text-[11px] text-zinc-500">
            <Bot className="w-3.5 h-3.5" />
            Powered by Claude Sonnet 4.5
          </div>
        )}
      </div>
    </aside>
  );
}

function UserFooter({ collapsed }) {
  const { user, logout } = useAuth();
  if (!user) return null;
  const initials = (user.name || user.email).slice(0,2).toUpperCase();
  const role = user.role || "super";
  const roleLabel = role === "super" ? "Super Admin" : "Admin Kantor";
  if (collapsed) {
    return (
      <div className="flex flex-col items-center gap-1.5" data-testid="user-footer" data-role={role}>
        <div
          className={`w-8 h-8 rounded-full text-white grid place-items-center text-[10px] font-bold ${role === "super" ? "bg-[#E65100]" : "bg-zinc-500"}`}
          title={`${user.email} · ${roleLabel}`}
        >
          {initials}
        </div>
        <button onClick={logout} title="Keluar" data-testid="logout-btn"
          className="text-zinc-400 hover:text-red-500">
          <LogOut className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 px-2 py-1.5 rounded-sm bg-zinc-50 border border-border" data-testid="user-footer" data-role={role}>
      <div className={`w-7 h-7 rounded-full text-white grid place-items-center text-[10px] font-bold ${role === "super" ? "bg-[#E65100]" : "bg-zinc-500"}`}>
        {initials}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-semibold truncate">{user.name || user.email.split("@")[0]}</div>
        <div className="text-[9px] text-zinc-500 truncate flex items-center gap-1">
          <span className={`inline-block rounded-sm px-1 py-0 font-bold uppercase tracking-widest ${role === "super" ? "text-[#E65100]" : "text-zinc-500"}`} data-testid="user-role-badge">
            {roleLabel}
          </span>
        </div>
      </div>
      <button onClick={logout} title="Keluar" data-testid="logout-btn"
        className="text-zinc-400 hover:text-red-500 shrink-0">
        <LogOut className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function Header() {
  const loc = useLocation();
  const title = NAV.find(n => (n.to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(n.to)))?.label || "Dashboard";
  return (
    <header className="h-14 border-b border-border bg-white flex items-center px-6 sticky top-0 z-20">
      <h1 className="font-['Manrope'] font-bold text-lg tracking-tight" data-testid="page-title">{title}</h1>
      <div className="ml-auto text-[11px] uppercase tracking-widest text-zinc-500 font-['IBM_Plex_Sans']">
        Ops Console · v1
      </div>
    </header>
  );
}

function Layout({ children }) {
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("sidebar_collapsed") === "1"; } catch { return false; }
  });
  const toggle = () => {
    setCollapsed(c => {
      const nv = !c;
      try { localStorage.setItem("sidebar_collapsed", nv ? "1" : "0"); } catch {}
      return nv;
    });
  };
  return (
    // v84.38 — Layout bg pakai `--muted` (~#26262C) yg SEDIKIT lebih terang
    // dari `--card` (~#1D1D22) supaya cards/sidebar/topbar tampak "duduk di atas"
    // background. Sesuai request user: gelap tapi sedikit lebih muda dari
    // bagian foreground.
    <div className="min-h-screen flex bg-muted">
      <Sidebar collapsed={collapsed} onToggle={toggle} />
      <div className="flex-1 flex flex-col min-w-0">
        <Header />
        <main className="p-6 flex-1 min-w-0">{children}</main>
      </div>
      <WODoneNotifier />
    </div>
  );
}

function AppGate() {
  const { user, tech, checked } = useAuth();
  const location = useLocation();

  if (!checked) {
    return (
      <div className="min-h-screen grid place-items-center bg-zinc-50" data-testid="auth-loading">
        <div className="text-[11px] uppercase tracking-widest text-zinc-500">Memuat…</div>
      </div>
    );
  }

  const path = location.pathname;
  const isTechRoute = path === "/tech-login" || path.startsWith("/tech/");

  // Tech route handling
  if (isTechRoute) {
    // /tech-login: public — kalau tech sudah login redirect ke /tech/rekap
    if (path === "/tech-login") {
      if (tech) return <Navigate to="/tech/rekap" replace />;
      return <TechLogin />;
    }
    // /tech/rekap (dan future /tech/*): wajib tech login
    if (!tech) return <Navigate to="/tech-login" replace />;
    return (
      <Routes>
        <Route path="/tech/rekap" element={<TechRekap />} />
        <Route path="*" element={<Navigate to="/tech/rekap" replace />} />
      </Routes>
    );
  }

  // Admin routes — belum login → tampilkan halaman Login admin
  if (!user) {
    return <Login />;
  }

  // Admin — full dashboard
  const isSuper = (user?.role || "super") === "super";
  const SuperGuard = ({ children }) => isSuper ? children : <Navigate to="/" replace />;
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/simulator" element={<Simulator />} />
        <Route path="/work-orders" element={<WorkOrders />} />
        <Route path="/technicians" element={<Technicians />} />
        <Route path="/live-map" element={<LiveMap />} />
        <Route path="/tarif" element={<Tarif />} />
        <Route path="/rekap-penagihan" element={<RekapPenagihan />} />
        <Route path="/billing-detail/:id" element={<BillingDetail />} />
        <Route path="/commissions" element={<Commissions />} />
        <Route path="/personel" element={<Personel />} />
        <Route path="/customer" element={<Customer />} />
        <Route path="/komisi-teknisi" element={<KomisiTeknisi />} />
        <Route path="/payroll" element={<Payroll />} />
        <Route path="/pesan-otomatis" element={<WaMessages />} />
        <Route path="/estimasi" element={<Estimasi />} />
        <Route path="/ai-rules" element={<SuperGuard><AIRules /></SuperGuard>} />
        <Route path="/llm-settings" element={<SuperGuard><LLMSettings /></SuperGuard>} />
        <Route path="/google-sync" element={<GoogleSync />} />
        <Route path="/qr-quick-order" element={<QRQuickOrderPage />} />
        <Route path="/admins" element={<SuperGuard><Admins /></SuperGuard>} />
        <Route path="/settings" element={<SuperGuard><SettingsPage /></SuperGuard>} />
      </Routes>
      <AdminAiChat />
    </Layout>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes (no auth) — must be OUTSIDE AuthProvider gate. */}
        <Route path="/billing/:token" element={<PublicBilling />} />
        <Route path="/view-invoice/:token" element={<ViewInvoice />} />
        <Route path="/track/:token" element={<Tracking />} />
        <Route path="/order/:token" element={<ErrorBoundary><QuickOrder /></ErrorBoundary>} />
        <Route path="/quick-order-public" element={<ErrorBoundary><QuickOrderPublic /></ErrorBoundary>} />
        <Route path="/tech/gps" element={<TechGps />} />
        {/* Convenience: /billing (tanpa token) → redirect ke tech-login.
            Mencegah user salah masuk ke Admin Login yang minta email. */}
        <Route path="/billing" element={<Navigate to="/tech-login" replace />} />
        <Route path="*" element={
          <AuthProvider>
            <AppGate />
          </AuthProvider>
        } />
      </Routes>
      <Toaster position="top-right" />
    </BrowserRouter>
  );
}

export default App;
